import type { DraftTask, DraftTaskReviewStatus } from '@frontend/api-types';
import { EArray, deserializeJson, match } from '@frontend/duck-tape';
import type { SocketClient } from '@frontend/sockets';
import { useEffect, useState } from 'react';
import { useCreateDraftTask } from '../context/CreateDraftTaskContext';
import type { CreateDraftTaskParams, CreateDraftTaskStep } from '../types';
import { generateEmptyInitialQuestionWithAnswer } from '../utils';

type DraftCreationFlowProps = {
  initialDraftTaskId?: string;
  onComplete?: () => void;
  onDraftAutoTransitioned?: (taskId?: string) => void;
  params?: CreateDraftTaskParams;
  socketClient: SocketClient;
};

export const useDraftCreationFlow = ({
  initialDraftTaskId,
  onComplete,
  onDraftAutoTransitioned,
  params,
  socketClient,
}: DraftCreationFlowProps) => {
  const [currentParentStep, setCurrentParentStep] = useState<CreateDraftTaskStep | undefined>();
  const [draftTaskId, setDraftTaskId] = useState<string | undefined>(initialDraftTaskId);
  const { disconnectSocket, getSocket, initializeSocket } = socketClient;
  const { setState: setCreateDraftTaskState, state, syncDraftTask } = useCreateDraftTask();

  const resolveExpectedParentStep = (draftTask: DraftTask): CreateDraftTaskStep => {
    return draftTask.reviewStatus &&
      EArray.contains(
        ['questions_ready', 'ready_for_scheduling'] satisfies DraftTaskReviewStatus[],
        draftTask.reviewStatus,
      )
      ? 'information_gathering'
      : 'description';
  };

  useEffect(() => {
    return () => disconnectSocket();
  }, [disconnectSocket]);

  useEffect(() => {
    setCreateDraftTaskState((prevState) => ({
      ...prevState,
      description: params?.description || prevState.description,
      example: params?.example,
      letterRecommendationId: params?.letterRecommendationId,
      letterRecommendationTaskSuggestionIdx: params?.letterRecommendationTaskSuggestionIdx,
      name: params?.name || prevState.name,
      suggestionId: params?.suggestionId,
    }));
    (async () => {
      if (draftTaskId) {
        const draftTask = await syncDraftTask(draftTaskId);
        if (draftTask) {
          setupSocket(draftTask.id);
          setCurrentParentStep(resolveExpectedParentStep(draftTask));
        }
      } else {
        setCurrentParentStep('description');
      }
    })();
  }, []);

  const setupSocket = (draftTaskId: string) => {
    const existingSocket = getSocket('draftTaskCreation');
    if (existingSocket) {
      // Noop if the socket already exists
      return existingSocket;
    }
    const socket = initializeSocket({
      channelType: 'draftTaskCreation',
      params: {
        draftId: draftTaskId,
      },
    });

    const onDraftStatusUpdateFailed = async () => {
      await syncDraftTask(draftTaskId);
    };

    const onDraftStatusUpdated = async () => {
      await syncDraftTask(draftTaskId);
    };

    const onDraftQuestionsReady = async () => {
      await syncDraftTask(draftTaskId);
    };

    socket.on('draft_status_update_failed', onDraftStatusUpdateFailed);
    socket.on('draft_status_updated', onDraftStatusUpdated);
    socket.on('draft_questions_ready', onDraftQuestionsReady);
    socket.on('draft_auto_transitioned', (response) => {
      if (response) {
        const deserializedIq = deserializeJson(response);
        if (deserializedIq?.taskId) {
          onDraftAutoTransitioned?.(deserializedIq.taskId);
          return;
        }
      }
      onDraftAutoTransitioned?.();
    });
    socket.on('first_initial_question_ready', async (firstIq) => {
      setCreateDraftTaskState((prev) => {
        const deserializedIq = deserializeJson(firstIq);
        const firstIqWithAnswer = generateEmptyInitialQuestionWithAnswer(deserializedIq.content.firstQuestion);
        if (!prev.draftTaskEntity?.aiQuestionnaire && firstIqWithAnswer) {
          return {
            ...prev,
            draftTaskEntity: prev.draftTaskEntity
              ? {
                  ...prev.draftTaskEntity,
                  aiQuestionnaire: {
                    // This will get synchronized to a real ownerId later on
                    ownerId: '',
                    responses: [deserializedIq.content.firstQuestion],
                  },
                  reviewStatus: 'questions_ready',
                }
              : undefined,
            initialQuestionsWithAnswers: [firstIqWithAnswer],
          };
        }
        return prev;
      });
    });

    return socket;
  };

  const decrementParentStep = () => {
    if (!currentParentStep) {
      return;
    }

    const getLastStep = (step: CreateDraftTaskStep): CreateDraftTaskStep | undefined => {
      if (step === 'description') return undefined;
      if (step === 'information_gathering') return 'description';
      if (step === 'checkout') return 'information_gathering';
      return undefined;
    };

    const lastStep = getLastStep(currentParentStep);
    if (lastStep) {
      setCurrentParentStep(lastStep);
    }
  };

  const startOver = () => {
    setCurrentParentStep('description');
  };

  const incrementParentStep = () => {
    if (!currentParentStep) {
      return;
    }

    match(currentParentStep)
      .with('description', () => setCurrentParentStep('information_gathering'))
      .with('information_gathering', () => setCurrentParentStep('checkout'))
      // This is the final step, we can navigate the user wherever we want from here
      .with('checkout', () => onComplete?.())
      .exhaustive();
  };

  return {
    currentParentStep,
    decrementParentStep,
    draftTaskState: state,
    incrementParentStep,
    setDraftTaskId,
    setupSocket,
    startOver,
    syncDraftTask,
  };
};
